import { useEffect, useRef, useState } from "react";
import { useI18n } from "../../utils/translate/i18n/useI18n";
import { useIntermittentTone } from "../../utils/useIntermittentTone";
import { useNoiseGenerator } from "../../utils/useNoiseGenerator";
import type { Ear } from "../../type/tonalTest";
import AudiometerChart from "./components/audiometerChart";
import AudiometerInfoModal from "./components/audiometerInfoModal";
import AudiometerNoiseSlider from "./components/audiometerNoiseSlider";
import {
  DB_LEVELS,
  DB_MIN,
  DB_MAX,
  DB_STEP,
  FREQUENCIES,
  BC_FREQ_MIN_INDEX,
  BC_FREQ_MAX_INDEX,
  type AudiometerCursor,
  type MeasurementMode
} from "./components/audiometerChartConstants";
import "./audiometer.scss";

const COLORS: Record<Ear, string> = {
  left: "#1a56db",
  right: "#e02424"
};

const OTHER_EAR: Record<Ear, Ear> = { left: "right", right: "left" };

const DEFAULT_FREQ_INDEX = FREQUENCIES.indexOf(1000);
const DEFAULT_DB_INDEX = DB_LEVELS.indexOf(40);

const createDefaultCursor = (): AudiometerCursor => ({
  freqIndex: DEFAULT_FREQ_INDEX,
  dbIndex: DEFAULT_DB_INDEX
});

type EarValues = { ac: (number | null)[]; bc: (number | null)[] };

const createEmptyEarValues = (): EarValues => ({
  ac: Array(FREQUENCIES.length).fill(null),
  bc: Array(FREQUENCIES.length).fill(null)
});

const clampFreqIndex = (freqIndex: number, mode: MeasurementMode) => {
  const min = mode === "BC" ? BC_FREQ_MIN_INDEX : 0;
  const max = mode === "BC" ? BC_FREQ_MAX_INDEX : FREQUENCIES.length - 1;
  return Math.min(max, Math.max(min, freqIndex));
};

const clampCursor = (current: AudiometerCursor, deltaFreq: number, deltaDb: number, mode: MeasurementMode): AudiometerCursor => ({
  freqIndex: clampFreqIndex(current.freqIndex + deltaFreq, mode),
  dbIndex: Math.min(DB_LEVELS.length - 1, Math.max(0, current.dbIndex + deltaDb))
});

export default function Audiometer() {
  const { t } = useI18n();
  const tone = useIntermittentTone();
  const noise = useNoiseGenerator();
  const [mode, setMode] = useState<MeasurementMode>("AC");
  const [values, setValues] = useState<Record<Ear, EarValues>>({
    left: createEmptyEarValues(),
    right: createEmptyEarValues()
  });
  const [cursor, setCursor] = useState<Record<Ear, AudiometerCursor>>({
    left: createDefaultCursor(),
    right: createDefaultCursor()
  });
  const [focusedEar, setFocusedEar] = useState<Ear>("left");
  const [soundEar, setSoundEar] = useState<Ear | null>(null);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [noiseEnabled, setNoiseEnabled] = useState(false);
  const [noiseDb, setNoiseDb] = useState(-10);

  const chartRefs: Record<Ear, React.RefObject<HTMLDivElement | null>> = {
    left: useRef<HTMLDivElement>(null),
    right: useRef<HTMLDivElement>(null)
  };

  useEffect(() => {
    chartRefs.left.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      tone.stop();
      noise.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Noise always targets the ear opposite the one currently focused.
  useEffect(() => {
    if (!noiseEnabled) return;
    noise.start(OTHER_EAR[focusedEar], noiseDb);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noiseEnabled, focusedEar]);

  // Keep cursors inside the allowed frequency range whenever the AC/BC mode changes.
  useEffect(() => {
    setCursor(prev => ({
      left: clampCursor(prev.left, 0, 0, mode),
      right: clampCursor(prev.right, 0, 0, mode)
    }));
  }, [mode]);

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;

      const key = e.key.toLowerCase();
      if (key === "l") {
        e.preventDefault();
        chartRefs.left.current?.focus();
      } else if (key === "r" || key === "p") {
        e.preventDefault();
        chartRefs.right.current?.focus();
      } else if (key === "a") {
        e.preventDefault();
        setMode("AC");
      } else if (key === "b") {
        e.preventDefault();
        setMode("BC");
      } else if (key === "s") {
        e.preventDefault();
        toggleNoise();
      } else if (e.key === "PageUp") {
        if (!noiseEnabled) return;
        e.preventDefault();
        handleNoiseChange(Math.min(DB_MAX, noiseDb + DB_STEP));
      } else if (e.key === "PageDown") {
        if (!noiseEnabled) return;
        e.preventDefault();
        handleNoiseChange(Math.max(DB_MIN, noiseDb - DB_STEP));
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noiseEnabled, noiseDb, focusedEar]);

  const moveCursor = (ear: Ear, deltaFreq: number, deltaDb: number) => {
    setCursor(prev => {
      const next = clampCursor(prev[ear], deltaFreq, deltaDb, mode);
      if (soundEar === ear) {
        tone.update(ear, FREQUENCIES[next.freqIndex], DB_LEVELS[next.dbIndex]);
      }
      return { ...prev, [ear]: next };
    });
  };

  const setCursorAbsolute = (ear: Ear, freqIndex: number, dbIndex: number) => {
    const clampedFreqIndex = clampFreqIndex(freqIndex, mode);
    setCursor(prev => {
      if (soundEar === ear) {
        tone.update(ear, FREQUENCIES[clampedFreqIndex], DB_LEVELS[dbIndex]);
      }
      return { ...prev, [ear]: { freqIndex: clampedFreqIndex, dbIndex } };
    });
  };

  const placePoint = (ear: Ear) => {
    setValues(prev => {
      const { freqIndex, dbIndex } = cursor[ear];
      const key = mode === "AC" ? "ac" : "bc";
      const nextArr = [...prev[ear][key]];
      nextArr[freqIndex] = DB_LEVELS[dbIndex];
      return { ...prev, [ear]: { ...prev[ear], [key]: nextArr } };
    });
  };

  const startTone = (ear: Ear) => {
    if (soundEar === ear) return;
    const { freqIndex, dbIndex } = cursor[ear];
    tone.start(ear, FREQUENCIES[freqIndex], DB_LEVELS[dbIndex]);
    setSoundEar(ear);
  };

  const stopTone = () => {
    tone.stop();
    setSoundEar(null);
  };

  const handleKeyDown = (ear: Ear) => (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case "ArrowUp":
        e.preventDefault();
        moveCursor(ear, 0, -1);
        break;
      case "ArrowDown":
        e.preventDefault();
        moveCursor(ear, 0, 1);
        break;
      case "ArrowLeft":
        e.preventDefault();
        moveCursor(ear, -1, 0);
        break;
      case "ArrowRight":
        e.preventDefault();
        moveCursor(ear, 1, 0);
        break;
      case "Enter":
      case " ":
      case "Spacebar":
        e.preventDefault();
        placePoint(ear);
        break;
      case "Control":
      case "Alt":
        e.preventDefault();
        startTone(ear);
        break;
      case "Tab":
        e.preventDefault();
        chartRefs[OTHER_EAR[ear]].current?.focus();
        break;
      default:
        break;
    }
  };

  const handleKeyUp = (ear: Ear) => (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Control" || e.key === "Alt") {
      e.preventDefault();
      if (soundEar === ear) stopTone();
    }
  };

  const handleBlur = (ear: Ear) => () => {
    if (soundEar === ear) stopTone();
  };

  const toggleNoise = () => {
    setNoiseEnabled(prev => {
      const next = !prev;
      if (!next) noise.stop();
      return next;
    });
  };

  const handleNoiseChange = (db: number) => {
    setNoiseDb(db);
    noise.update(OTHER_EAR[focusedEar], db);
  };

  const handlePointClick = (ear: Ear) => (freqIndex: number, dbIndex: number) => {
    setCursorAbsolute(ear, freqIndex, dbIndex);
  };

  return (
    <div className="audiometer">
      <div className="audiometer__intro">
        <h1>
          {t("Audiometer")}
          <button
            type="button"
            className="audiometer__info-btn"
            onClick={() => setIsInfoOpen(true)}
            aria-label={t("Jak korzystać z Audiometera")}
            title={t("Jak korzystać z Audiometera")}
          >
            <i className="bi bi-info-lg" aria-hidden="true" />
          </button>
        </h1>
      </div>

      <div className="audiometer__controls">
        <div className="audiometer__mode-switch" role="group" aria-label={t("Tryb pomiaru")}>
          <button
            type="button"
            className={`audiometer__mode-btn${mode === "AC" ? " audiometer__mode-btn--active" : ""}`}
            aria-pressed={mode === "AC"}
            onClick={() => setMode("AC")}
            title={t("Przewodnictwo powietrzne (słuchawki nagłowne)")}
          >
            <u>A</u>C
          </button>
          <button
            type="button"
            className={`audiometer__mode-btn${mode === "BC" ? " audiometer__mode-btn--active" : ""}`}
            aria-pressed={mode === "BC"}
            onClick={() => setMode("BC")}
            title={t("Przewodnictwo kostne (słuchawka kostna), zakres 250–4000 Hz")}
          >
            <u>B</u>C
          </button>
        </div>
        <div className="audiometer__mode-switch audiometer__mode-switch--single">
          <button
            type="button"
            className={`audiometer__mode-btn${noiseEnabled ? " audiometer__mode-btn--active" : ""}`}
            aria-pressed={noiseEnabled}
            onClick={toggleNoise}
            title={t("Szum maskujący dla drugiego ucha")}
          >
            <u>S</u>ZUM
          </button>
        </div>
      </div>

      <div className="audiometer__charts">
        {noiseEnabled && focusedEar === "right" && (
          <AudiometerNoiseSlider
            label={t("Szum maskujący dla drugiego ucha")}
            value={noiseDb}
            onChange={handleNoiseChange}
          />
        )}
        <AudiometerChart
          ear="left"
          color={COLORS.left}
          label={t("Ucho lewe")}
          acValues={values.left.ac}
          bcValues={values.left.bc}
          cursor={cursor.left}
          focused={focusedEar === "left"}
          cursorVisible={soundEar === "left" ? tone.isOn : true}
          containerRef={chartRefs.left}
          onFocus={() => setFocusedEar("left")}
          onBlur={handleBlur("left")}
          onKeyDown={handleKeyDown("left")}
          onKeyUp={handleKeyUp("left")}
          onPointClick={handlePointClick("left")}
        />
        <AudiometerChart
          ear="right"
          color={COLORS.right}
          label={t("Ucho prawe")}
          acValues={values.right.ac}
          bcValues={values.right.bc}
          cursor={cursor.right}
          focused={focusedEar === "right"}
          cursorVisible={soundEar === "right" ? tone.isOn : true}
          containerRef={chartRefs.right}
          onFocus={() => setFocusedEar("right")}
          onBlur={handleBlur("right")}
          onKeyDown={handleKeyDown("right")}
          onKeyUp={handleKeyUp("right")}
          onPointClick={handlePointClick("right")}
        />
        {noiseEnabled && focusedEar === "left" && (
          <AudiometerNoiseSlider
            label={t("Szum maskujący dla drugiego ucha")}
            value={noiseDb}
            onChange={handleNoiseChange}
          />
        )}
      </div>

      <AudiometerInfoModal open={isInfoOpen} onClose={() => setIsInfoOpen(false)} />
    </div>
  );
}
